import React, { Fragment } from 'react';
import { TableWrapper, Header, Table } from '../Styles/SummaryPortfolioModelStyles';
import { ThemeContext } from '@fluentui/react-theme-provider';
import { useContext, useEffect, useState } from 'react';
import { setCurrency } from '../../../../utils/numberFormatter';
import IroniaPoints from '../../../Portfolios/Summary/IroniaPoints';
import { ChevronDownIcon, ChevronUpIcon } from "@fluentui/react-icons-mdl2";
import { ReactComponent as IncrementIcon } from '../../../../images/increment.svg';
import { ReactComponent as DecrementIcon } from '../../../../images/decrement.svg';
import { Link } from 'react-router-dom';

export interface IPortfolioModelTable
{
    nameFund: string;
    isin: string;
    portfolioPercentage: number;
    ytdLinearReturn?: number;
    oneYearVolatility?: number;
    fundPrice: number;
    liquidationDate: string;
    currency: string;
    ironiaPoints: number;
    portfolioName?: string;
    modelPortfolioId?: number;
}

const PortfolioModelTable = ({data, isLoading, showPortfolioColumn = false}: {data: IPortfolioModelTable[], isLoading: boolean, showPortfolioColumn?: boolean}) =>
{
    const theme = useContext(ThemeContext);
    const [expandedPortfolios, setExpandedPortfolios] = useState<{[key: string]: boolean}>({});

    // Inicializar todas las carteras como expandidas cuando cambian los datos
    useEffect(() =>
    {
        if(showPortfolioColumn && data.length > 0)
        {
            const portfolioNames = Array.from(new Set(data.map(item => item.portfolioName).filter(Boolean)));
            const initialExpanded: {[key: string]: boolean} = {};
            portfolioNames.forEach(name =>
            {
                if (name) initialExpanded[name] = true;
            });
            setExpandedPortfolios(initialExpanded);
        }
    }, [data, showPortfolioColumn]);

    const togglePortfolio = (portfolioName: string) =>
    {
        setExpandedPortfolios(prev =>
        ({
            ...prev,
            [portfolioName]: !prev[portfolioName]
        }));
    };

    if(data.length === 0)
    {
        return(
            <TableWrapper theme={theme}>
                <Header>
                    <h3>Composición de tu Cartera Modelo</h3>
                </Header>
                {isLoading ?
                (
                    <p>Cargando datos de la cartera...</p>
                ) :
                (
                    <p>Selecciona al menos una Cartera Modelo para ver sus datos</p>
                )}
            </TableWrapper>
        );
    }

    if(showPortfolioColumn)
    {
        const groupedData = data.reduce((acc, item) =>
        {
            const portfolioName = item.portfolioName || 'Sin nombre';
            if(!acc[portfolioName])
            {
                acc[portfolioName] = [];
            }
            acc[portfolioName].push(item);
            return acc;
        }, {} as {[key: string]: IPortfolioModelTable[]});

        return(
            <TableWrapper theme={theme}>
                <Header>
                    <h3>Composición de tus Carteras Modelo</h3>
                </Header>
                <Table theme={theme}>
                    <thead>
                        <tr>
                            <th>Cartera Modelo / Nombre del Fondo</th>
                            <th>ISIN</th>
                            <th>Porcentaje en la Cartera</th>
                            <th>RTB YTD</th>
                            <th>Vola 1Y</th>
                            <th>Valor Liquidativo</th>
                            <th>Fecha de Valor Liquidativo</th>
                            <th>Divisa</th>
                            <th>Puntos IronIA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(groupedData).map(([portfolioName, funds]) =>
                        (
                            <Fragment key={portfolioName}>

                                <tr style={
                                    { 
                                        backgroundColor: theme?.palette?.neutralLighter || '#f8f9fa',
                                        cursor: 'pointer',
                                        fontWeight: 'bold'
                                    }}
                                    onClick={() => togglePortfolio(portfolioName)}
                                >
                                    <td style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                                        {expandedPortfolios[portfolioName] ? 
                                            <ChevronUpIcon style={{ fontSize: '14px' }} /> : 
                                            <ChevronDownIcon style={{ fontSize: '14px' }} />
                                        }
                                        <span>{portfolioName}</span>
                                        <span>({funds.length} fondos)</span>
                                    </td>
                                    <td colSpan={8}></td>
                                </tr>
                                {/* Fondos de la cartera (solo si está expandida) */}
                                {expandedPortfolios[portfolioName] && funds.map((fund, index) =>
                                (
                                    <tr key={`${portfolioName}-${index}`}>
                                        <td style={{ paddingLeft: '2rem' }}><Link to={`/${fund.isin}`}>{fund.nameFund}</Link></td>
                                        <td>{fund.isin}</td>
                                        <td>{fund.portfolioPercentage.toFixed(2).replace('.', ',')}%</td>
                                        <td>{fund.ytdLinearReturn !== undefined ?
                                            (
                                                <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                                                    <span>{fund.ytdLinearReturn.toFixed(2).replace('.', ',')}%</span>
                                                    {fund.ytdLinearReturn < 0 && <DecrementIcon />}
                                                    {fund.ytdLinearReturn > 0 && <IncrementIcon />}
                                                </div>
                                            )
                                            : '-'}
                                         </td>
                                        <td>{fund.oneYearVolatility !== undefined ? `${fund.oneYearVolatility.toFixed(2).replace('.', ',')}%` : '-'}</td>
                                        <td>{setCurrency('EUR', fund.fundPrice)}</td>
                                        <td>{fund.liquidationDate}</td>
                                        <td>{fund.currency}</td>
                                        <td><IroniaPoints score={fund.ironiaPoints} /></td>
                                    </tr>
                                ))}
                            </Fragment>
                        ))}
                    </tbody>
                </Table>
            </TableWrapper>
        );
    }
    
    return(
        <TableWrapper theme={theme}>
            <Header>
                <h3>Composición de tu Cartera Modelo</h3>
            </Header>
            <Table theme={theme}>
                <thead>
                    <tr>
                        <th>Nombre del Fondo</th>
                        <th>ISIN</th>
                        <th>Porcentaje en la Cartera</th>
                        <th>RTB YTD</th>
                        <th>Vola 1Y</th>
                        <th>Valor Liquidativo</th>
                        <th>Fecha de Valor Liquidativo</th>
                        <th>Divisa</th>
                        <th>Puntos IronIA</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((item, index) =>
                    (
                        <tr key={index}>
                            <td><Link to={`/${item.isin}`}>{item.nameFund}</Link></td>
                            <td>{item.isin}</td>
                            <td>{item.portfolioPercentage.toFixed(2).replace('.', ',')}%</td>
                            <td>{item.ytdLinearReturn !== undefined ?
                                (
                                    <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                                        <span>{item.ytdLinearReturn.toFixed(2).replace('.', ',')}%</span>
                                        {item.ytdLinearReturn < 0 && <DecrementIcon />}
                                        {item.ytdLinearReturn > 0 && <IncrementIcon />}
                                    </div>
                                )
                                : '-'}
                            </td>
                            <td>{item.oneYearVolatility !== undefined ? `${item.oneYearVolatility.toFixed(2).replace('.', ',')}%` : '-'}</td>
                            <td>{setCurrency('EUR',item.fundPrice)}</td>
                            <td>{item.liquidationDate}</td>
                            <td>{item.currency}</td>
                            <td><IroniaPoints score={item.ironiaPoints} /></td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </TableWrapper>
    );
}

export default PortfolioModelTable;